import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function HowTo() {
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'How to Spot a Fake Recruiter — ScamView Guide'
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              How to Use ScamView
            </h1>
            <p className="text-xl text-gray-300">
              Protect yourself from recruitment scams in 3 simple steps
            </p>
          </div>

          {/* Quick Start */}
          <div className="card bg-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Quick Start Guide</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Enter the Contact Information</h3>
                  <p className="text-gray-300">
                    Paste the recruiter's email address or phone number into the scan form.
                    You can scan email addresses and phone numbers.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Add Context (Optional but Recommended)</h3>
                  <p className="text-gray-300">
                    Paste the message content they sent you. This helps our AI analyze urgency patterns,
                    financial requests, and language red flags for more accurate results.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Review the Results</h3>
                  <p className="text-gray-300">
                    Get an instant risk score (1-10), AI analysis summary, detection signals,
                    and a clear recommendation on whether to engage.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Understanding Results */}
          <div className="card">
            <h2 className="text-2xl font-bold text-white mb-6">Understanding Your Results</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="text-green-400">●</span> Safe (1-3)
                </h3>
                <p className="text-gray-300 ml-6">
                  Appears legitimate. Verified domain, professional communication, no major red flags.
                  Stay vigilant but likely safe to proceed.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="text-yellow-400">●</span> Suspicious (4-6)
                </h3>
                <p className="text-gray-300 ml-6">
                  Mixed signals detected. Proceed with caution. Verify through official channels
                  before sharing personal information.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="text-orange-400">●</span> High Risk (7-8)
                </h3>
                <p className="text-gray-300 ml-6">
                  Likely a scam. Multiple red flags detected. Do not engage without thorough verification.
                  Report and block if possible.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="text-red-400">●</span> Critical (9-10)
                </h3>
                <p className="text-gray-300 ml-6">
                  Definite scam. Clear fraud indicators present. Do not engage. Report immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div className="card">
            <h2 className="text-2xl font-bold text-white mb-6">Best Practices</h2>
            <div className="space-y-3 text-gray-300">
              <div className="flex items-start gap-3">
                <span className="text-white mt-1">✓</span>
                <p>
                  <strong className="text-white">Scan before responding:</strong> Always verify recruiters
                  before sharing personal information or scheduling interviews.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-white mt-1">✓</span>
                <p>
                  <strong className="text-white">Include message content:</strong> The more context you provide,
                  the more accurate the analysis will be.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-white mt-1">✓</span>
                <p>
                  <strong className="text-white">Trust your gut:</strong> ScamView is a tool to help you decide,
                  not a replacement for your judgment. If something feels off, it probably is.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-white mt-1">✓</span>
                <p>
                  <strong className="text-white">Verify independently:</strong> For important opportunities,
                  verify the company and recruiter through LinkedIn or the company's official website.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-white mt-1">✓</span>
                <p>
                  <strong className="text-white">Never pay upfront:</strong> Legitimate employers never ask
                  for money upfront for equipment, training, or background checks.
                </p>
              </div>
            </div>
          </div>

          {/* Common Scam Patterns */}
          <div className="card bg-red-500/10 border-red-500/20">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <svg className="w-6 h-6 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Common Scam Patterns to Watch For
            </h2>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Equipment scams:</strong> They send a fake check and ask you to buy equipment from their "vendor"</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Urgency tactics:</strong> "Must respond within 24 hours" or "immediate hiring"</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Generic greetings:</strong> "Dear Applicant" instead of your name</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Free email addresses:</strong> Using Gmail/Yahoo instead of company domain</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Poor grammar:</strong> Obvious spelling and grammar mistakes</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span><strong className="text-white">Too good to be true:</strong> Extremely high pay for entry-level work</span>
              </li>
            </ul>
          </div>

          {/* Paid Plan Features */}
          <div className="card">
            <h2 className="text-2xl font-bold text-white mb-6">Paid Plan Features</h2>

            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Basic & Premium</h3>
            <div className="space-y-4 text-gray-300 mb-8">
              <div>
                <h4 className="font-semibold text-white mb-2">Phone Number Intelligence</h4>
                <p>
                  Scan phone numbers to detect VOIP services and known scam prefixes
                  (like Jamaica lottery scams from 1-876 numbers).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Community Reviews</h4>
                <p>
                  See and contribute community notes about known scammers. Help protect other job seekers.
                </p>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-emerald-400 mb-3">Premium Only</h3>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="font-semibold text-white mb-2">Email Forwarding (HireMail)</h4>
                <p>
                  Get your own @hiremail.net alias to forward recruiter emails for automatic analysis.
                  Protect your real email address from exposure.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pt-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary text-lg mb-4"
            >
              Try Your First Scan
            </button>
            <p className="text-gray-400 text-sm">
              No registration required for your first scan
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
